/*----------------------------------
- TOOLS MENU CATALOG (SSOT)
-----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import type { LandingMenuGroup } from './menuPanels';

/*----------------------------------
- TYPES
-----------------------------------*/

/**
 * A header Tools row. A row either links somewhere (`href`) or announces itself as
 * unbuilt (`status`), never both: a shipped tool must not still read "Coming soon",
 * and an unbuilt one must never be a dead link.
 *
 * A row carries no action note at its right and its tile is grey like every menu tile: the
 * "Appraise a name →" notes and the ink tiles left with the redraw of 2026-11-02 (mock parity
 * web-recheck-03), on the desktop panel and in the phone sheet alike (decision E2 = B).
 */
export type LandingToolsMenuItem = {
    id: string;
    label: string;
    description: string;
    iconName: IconName;
} & (
    | { href: string; status?: undefined }
    | { href?: undefined; status: 'Coming soon' }
);

/*----------------------------------
- CATALOG
-----------------------------------*/

/** The Tools menu in its two groups, the one order of the desktop panel and the phone sheet (web-recheck-03, E2 = B). */
export const LANDING_TOOLS_MENU_GROUPS: readonly LandingMenuGroup<LandingToolsMenuItem>[] = [
    {
        id: 'value-and-brand',
        title: 'Value and brand',
        items: [
            {
                id: 'multi-appraisal',
                label: 'Multi-Appraisal',
                description: 'Run one name or a list through several appraisers and read them on one axis.',
                iconName: 'chart-bar',
                href: LANDING_WEBSITE_PAGE_ROUTES.productAppraise,
            },
            {
                id: 'brand-logo-generator',
                label: 'Brand logo generator',
                description: 'A first logo for a name you are considering, to see it as a brand.',
                iconName: 'pencil',
                status: 'Coming soon',
            },
        ],
    },
    {
        id: 'look-up-a-name',
        title: 'Look up a name',
        items: [
            // Shipped 2026-10-09 (ADR-0078): one live lookup a day each for a visitor.
            {
                id: 'whois-rdap',
                label: 'WHOIS / RDAP checker',
                description: 'Who holds a domain, when it was registered and when it expires.',
                iconName: 'file-text',
                href: LANDING_WEBSITE_PAGE_ROUTES.whois,
            },
            {
                id: 'availability-check',
                label: 'Availability checker',
                description: 'Is this name free to register, and on which endings?',
                iconName: 'check',
                href: LANDING_WEBSITE_PAGE_ROUTES.availability,
            },
        ],
    },
];

/** Every Tools row in the groups' order. */
export const LANDING_TOOLS_MENU_ITEMS: readonly LandingToolsMenuItem[] = LANDING_TOOLS_MENU_GROUPS.flatMap((group) => group.items);

/**
 * The pages the Tools trigger lights up on. Derived from the catalog so a new tool
 * cannot ship with a menu row and a dead highlight; the unbuilt rows have no page.
 */
export const LANDING_TOOLS_MENU_ACTIVE_PATH_PREFIXES: readonly string[] = LANDING_TOOLS_MENU_ITEMS.reduce<string[]>(
    (paths, item) => (item.href ? [...paths, item.href] : paths),
    [],
);
